import dayjs from "dayjs";
import { ArrowUpRight } from "lucide-react";

import {
  artistAuditEventHref,
  artistAuditWikidataHref,
} from "@/lib/tina/artist-audit";
import type { ArtistAuditEvidence } from "@/types/tina";

interface DeceasedDetailsProps {
  evidence: ArtistAuditEvidence;
}

/**
 * The extra half of a "Possibly deceased" card: the Wikidata page to verify the death on,
 * and the future events that triggered it, each linked to its IMS page.
 *
 * Advisory only. Most of these are tribute or birthday nights, or a composer's work being
 * performed — the curator's answer to those is Dismiss. Wikidata is open-edit, so the link
 * comes first: a vandalised date of death would put a living act here.
 */
export const DeceasedDetails = ({ evidence }: DeceasedDetailsProps) => {
  const wikidataHref = artistAuditWikidataHref(
    evidence.wikidata_url,
    evidence.wikidata_qid,
  );
  // Wire data from a possibly-newer orchestrator: never assume the array is there.
  const events = Array.isArray(evidence.future_events)
    ? evidence.future_events
    : [];
  const total = evidence.future_event_count ?? events.length;
  return (
    <div className="flex flex-col gap-0.5 text-xs">
      <div className="flex items-center gap-2">
        {wikidataHref ? (
          <a
            href={wikidataHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 hover:underline"
            onClick={(event) => event.stopPropagation()}
            title="Verify the date of death on Wikidata — it is open-edit and can be wrong"
          >
            Wikidata {evidence.wikidata_qid ?? ""}
            <ArrowUpRight className="h-3 w-3" />
          </a>
        ) : null}
        {evidence.died_on ? (
          <span className="text-muted-foreground">
            died {dayjs(evidence.died_on).format("DD/MM/YYYY")}
          </span>
        ) : null}
      </div>
      {events.map((event) => {
        const href = artistAuditEventHref(event.event_id);
        const label = event.name || event.event_id;
        return (
          <div key={event.event_id} className="flex items-baseline gap-1.5">
            <span className="shrink-0 font-mono text-[10px] tabular-nums text-muted-foreground">
              {event.start_at
                ? dayjs(event.start_at).format("DD/MM/YYYY HH:mm")
                : "date unknown"}
            </span>
            {href ? (
              <a
                href={href}
                className="inline-flex items-center gap-1 truncate hover:underline"
                onClick={(clickEvent) => clickEvent.stopPropagation()}
                title="Open the event"
              >
                {label}
                <ArrowUpRight className="h-3 w-3 shrink-0" />
              </a>
            ) : (
              <span className="truncate">{label}</span>
            )}
          </div>
        );
      })}
      {total > events.length ? (
        <span className="text-muted-foreground">
          … and {total - events.length} more
        </span>
      ) : null}
    </div>
  );
};
